import React, { useEffect, useState } from 'react';

import { CTA, Infobox, Spinner } from '@vw-marketing/us-components';

import {
  createAdditionalPromises,
  getCarImageFromServiceConfig,
  getModelCode,
  parseMileageNumber,
  validateFormInput,
} from '../../utils/general';
import { fetchLiterature } from '../../fetch-data/fetch-literature';
import { fetchMaintenanceSchedule } from '../../fetch-data/fetch-maintenance-schedule';
import { fetchModelYear } from '../../fetch-data/fetch-model-year';
import { groupMaintenanceItemsByInterval } from '../../utils/maintenance-schedule-utils';
import {
  Literature,
  MaintenanceSchedule,
  ModelYear,
} from '../../typings/general';
import { Mileage } from '../../components/mileage';
import {
  onVWBasicButtonClickEvent,
} from '../../tagging/tagging-helpers';
import { SectionHeader } from '../../components/sectionHeader';
import { TabSelector } from '../../components/tabSelector';
import { useDeeplinkParams } from '../../hooks/use-deeplink-params';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import {
  StyledContentContainer,
  StyledFormContainer,
  StyledInfoboxContainer,
  StyledMainContainer,
  StyledSubmitButton,
} from './styled';
import IncentiveConstants from '../../hooks-store/incentive-store/actions/incentive-constants';

interface FormsProps {
  setLiteratureData: (value: Literature[]) => void;
  setMaintenanceSchedule: (value: MaintenanceSchedule) => void;
  baseUrl?: string;
  apiConfig?: string;
}

export const Form = ({
  setLiteratureData,
  setMaintenanceSchedule,
  apiConfig = ''
}: FormsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [vinApiError, setVinApiError] = useState<string | null>(null);
  const [lastMileage, setLastMileage] = useState<string | null>(null);
  const [maxMileageExceeded, setMaxMileageExceeded] = useState<boolean>(false);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);

  const trackingManager = useTrackingManager();
  const { mode } = useFeatureAppConfig();
  const { modelYear: deeplinkModelYear } = useDeeplinkParams();
  const { translationHandler } = useMaintenanceContext();
  const { 'service-config-provider': serviceProvider } = useFeatureServices();
  const { configs } = serviceProvider;
  const faServicesUrl = configs['fa-services']?.urlOrigin ?? '';
  const [
    store,
    {
      [IncentiveConstants.SetModelCardData]: updateModelCardData,
      [IncentiveConstants.SetModelYears]: setModelYears,
      [IncentiveConstants.SetVehicleData]: setVehicleData,
      [IncentiveConstants.UpdateStoreByFormInput]: updateStoreByFormInput,
      [IncentiveConstants.ValidateForm]: validateForm,
    },
  ] = useStore();
  const { form, modelYears, currentData } = store;

  useEffect(() => {
    if (!apiConfig) return;

    if (currentData?.modelYears?.length! <= 0 || modelYears?.length <= 0) {
      fetchModelYear({ faServicesUrl, apiKey: apiConfig }).then((result: ModelYear) => {
        const { model_years } = result;
        setModelYears({ modelYears: model_years });
        updateModelCardData({
          modelYears: model_years,
        });

        const isDeeplinkYearInvalid =
          deeplinkModelYear &&
          form.modelYear === deeplinkModelYear &&
          !model_years.some((year) => year.value === deeplinkModelYear);

        if (isDeeplinkYearInvalid) {
          updateStoreByFormInput({ ...form, modelYear: undefined, model: undefined });
        }
      });
    }
  }, [apiConfig]);

  const validateInputs = () => {
    const { mileage, vin, model, modelYear, trim } = validateFormInput(
      form,
      mode,
    );
    validateForm({
      status: 'submitted',
      formValidationError: {
        mileage: !mileage || maxMileageExceeded,
        vin: !vin,
        model: !model,
        modelYear: !modelYear,
        trim: !trim,
      },
    });
    return {
      mileage,
      vin,
      model,
      modelYear,
      trim,
    };
  };

  const handleResults = (
    isVinMode: boolean,
    mainResult: any,
    additionalResults: any[],
  ) => {
    const serviceConfig = additionalResults[0];

    if (!isVinMode && serviceConfig) {
      getCarImageFromServiceConfig(
        serviceConfig,
        form.model!,
        form.modelYear!,
        updateModelCardData,
      );
    }

    if (isVinMode) {
      const vehicleInfo = additionalResults[0];
      const carImage = additionalResults[1];

      updateModelCardData({
        ...vehicleInfo,
        ...(carImage ? { carImage } : {}),
      });
    }

    if (mode === 'literature') {
      const documents = mainResult.ownersLiteratureDocuments || [];
      setLiteratureData(documents);
      setNoDataFound(documents.length === 0);
      return true;
    } else {
      const packages = mainResult?.packages ?? [];
      setNoDataFound(packages.length === 0);

      if (mode === 'maintenance') {
        const intervalValues = packages.map((pkg) =>
          parseInt(pkg.interval.value, 10),
        );
        const maxMileage =
          intervalValues.length > 0 ? Math.max(...intervalValues) : null;

        const enteredMileage = parseMileageNumber(form.mileage) ?? 0;

        if (maxMileage && enteredMileage > maxMileage) {
          setLastMileage(maxMileage.toString());
          setMaxMileageExceeded(true);

          validateForm({
            status: 'submitted',
            formValidationError: {
              ...store.formValidationError,
              mileage: true,
            },
          });

          return false;
        } else {
          setLastMileage(null);
          setMaxMileageExceeded(false);
        }
      }

      const groupedPackages = groupMaintenanceItemsByInterval(packages);

      setMaintenanceSchedule({
        packages: groupedPackages,
        time_based_services: mainResult?.time_based_services ?? [],
      });

      return true;
    }
  };

  const onSubmit = async () => {
    setVinApiError(null);
    setLastMileage(null);
    setMaxMileageExceeded(false);
    setNoDataFound(false);

    const { mileage, vin, model, modelYear, trim } = validateInputs();
    const { selectedTab } = form;
    const isVinMode = selectedTab === 'vin';
    const isModelDetailsMode = selectedTab === 'modelDetails';

    const isValid =
      ((isModelDetailsMode && model && trim && modelYear) ||
        (isVinMode && vin)) &&
      mileage;

    if (!isValid) return;

    try {
      setIsLoading(true);

      const commonParams = {
        faServicesUrl,
        apiKey: apiConfig,
        modelYear: !isVinMode ? form.modelYear : undefined,
        modelCode: !isVinMode ? getModelCode(form.trim) : undefined,
        vin: isVinMode ? form.vin : undefined,
      };

      const [mainResult, ...additionalResults] = await Promise.all([
        mode === 'literature'
          ? fetchLiterature(commonParams).catch((error) => {
            if (isVinMode) {
              setVinApiError(translationHandler('form.vin.error'));
            }
            throw error;
          })
          : fetchMaintenanceSchedule({
            ...commonParams,
            odometerValue: parseMileageNumber(form.mileage) ?? 0,
            salesModelCode: !isVinMode ? form.trim : undefined,
          }).catch((error) => {
            if (isVinMode) {
              setVinApiError(translationHandler('form.vin.error'));
            }
            throw error;
          }),
        ...createAdditionalPromises(isVinMode, faServicesUrl, apiConfig, form.vin),
      ]);

      const isResultValid = handleResults(
        isVinMode,
        mainResult,
        additionalResults,
      );

      if (isResultValid) {
        const vehicleData = {
          modelName: isVinMode ? additionalResults[0].model : form.model,
          modelYear: isVinMode
            ? additionalResults[0].modelYear
            : form.modelYear,
          trim: isVinMode ? additionalResults[0].trim : currentData.trim,
          vin: form.vin,
          odometer: form.mileage ?? 0,
        }        

        // Save vehicle data to store
        setVehicleData(vehicleData);

        onVWBasicButtonClickEvent(
          trackingManager,
          {
            Type: 'Button',
            Name: translationHandler(
              mode === 'maintenance'
                ? 'form.submit.maintenance'
                : 'form.submit.literature',
            ),
            ItemCount: 1,
            ItemPosition: 1,
          },
          vehicleData,
          mode,
        );
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledMainContainer id="maintenanceFA">
      <StyledFormContainer>
        <StyledContentContainer className="form-content">
          <SectionHeader />
          <TabSelector
            onSelect={(tab) => {
              setVinApiError(null);
              setLastMileage(null);
              setMaxMileageExceeded(false);
              setNoDataFound(false);
              updateStoreByFormInput({ ...form, selectedTab: tab });
            }}
            vinApiError={vinApiError}
            apiConfig={apiConfig}
            deeplinkModelYear={deeplinkModelYear}
          />
          {mode === 'maintenance' && (
            <Mileage
              lastMileage={lastMileage}
              maxMileageExceeded={maxMileageExceeded}
            />
          )}
          {noDataFound && (
            <StyledInfoboxContainer>
              <Infobox>
                {translationHandler(
                  mode === 'maintenance'
                    ? 'form.maintenance.no.data.error'
                    : 'form.literature.no.data.error',
                )}
              </Infobox>
            </StyledInfoboxContainer>
          )}
          <StyledSubmitButton>
            <CTA
              tag="button"
              type="submit"
              stretchContent
              onClick={onSubmit}
              icon={isLoading ? <Spinner /> : undefined}
              disabled={isLoading}
            >
              {mode === 'maintenance'
                ? translationHandler('form.submit.maintenance')
                : translationHandler('form.submit.literature')}
            </CTA>
          </StyledSubmitButton>
        </StyledContentContainer>
      </StyledFormContainer>
    </StyledMainContainer>
  );
};
